"use client";

import { useMemo, useRef, useState } from "react";
import { z } from "zod";
import {
  evaluateCalculator,
  type CalculatorInputDefinition,
  type CalculatorInputValues,
  type CalculatorSchema,
} from "@/lib/gamification/calculator-engine";
import {
  captureGamificationResultForCrm,
  gamificationTrackingAttributes,
  trackGamificationEngagement,
  type GamificationTrackingContext,
} from "@/components/gamification/tracking";
import PlayableExperienceCanvas from "@/components/gamification/PlayableExperienceCanvas";
import { useWebMcpTool } from "@/lib/webmcp/react";

export type CalculatorToolCopy = {
  readonly eyebrow: string;
  readonly title: string;
  readonly intro: string;
  readonly calculateLabel: string;
  readonly resultLabel: string;
  readonly bandLabel: string;
  readonly resetLabel: string;
};

type CalculatorToolProps = {
  readonly copy: CalculatorToolCopy;
  readonly schema: CalculatorSchema;
  readonly tracking: GamificationTrackingContext;
};

function defaultValueFor(input: CalculatorInputDefinition): number | string {
  return input.kind === "number" ? input.defaultValue : input.defaultOptionId;
}

function initialValues(schema: CalculatorSchema): CalculatorInputValues {
  return Object.fromEntries(schema.inputs.map((input) => [input.id, defaultValueFor(input)]));
}

function displayValueFor(input: CalculatorInputDefinition, value: number | string | undefined) {
  if (input.kind === "number") {
    return value ?? input.defaultValue;
  }

  const selectedId = typeof value === "string" && value ? value : input.defaultOptionId;
  return input.options.find((option) => option.id === selectedId)?.label ?? input.options[0]?.label;
}

export default function CalculatorTool({ copy, schema, tracking }: CalculatorToolProps) {
  const [values, setValues] = useState<CalculatorInputValues>(() => initialValues(schema));
  const [touchedIds, setTouchedIds] = useState<readonly string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const startedRef = useRef(false);
  const evaluation = useMemo(() => evaluateCalculator(schema, values), [schema, values]);
  const inputProgress = schema.inputs.length > 0 ? Math.round((touchedIds.length / schema.inputs.length) * 100) : 0;
  const canvasNodes = schema.inputs.map((input) => ({
    id: input.id,
    label: input.label,
    detail: input.description,
    value: displayValueFor(input, values[input.id]),
    active: touchedIds.includes(input.id),
    complete: submitted || touchedIds.includes(input.id),
  }));
  const canvasTracks = evaluation.metrics.map((metric) => ({
    id: metric.id,
    label: metric.label,
    value: metric.band?.label ?? metric.displayValue,
    complete: submitted,
  }));
  const primaryBandLabel = evaluation.metrics.find((metric) => metric.band)?.band?.label;
  const toolSchema = useMemo(
    () =>
      z
        .object(
          Object.fromEntries(
            schema.inputs.map((input) => [
              input.id,
              input.kind === "number"
                ? z.number().min(input.min).max(input.max).multipleOf(input.step ?? 1)
                : z.enum(input.options.map((option) => option.id) as [string, ...string[]]),
            ]),
          ) as z.ZodRawShape,
        )
        .strict(),
    [schema.inputs],
  );
  useWebMcpTool({
    action: "run_gamification_calculator",
    instance: schema.id.replaceAll("-", "_"),
    schema: toolSchema,
    execute: (input) => {
      const nextValues = input as CalculatorInputValues;
      const nextEvaluation = evaluateCalculator(schema, nextValues);
      setValues(nextValues);
      setTouchedIds(schema.inputs.map((item) => item.id));
      setSubmitted(true);
      return { status: "success", uiState: nextEvaluation };
    },
  });

  function trackStart() {
    if (startedRef.current) {
      return;
    }

    startedRef.current = true;
    trackGamificationEngagement(tracking, `${tracking.componentId}.start`, { xp_zone: "progress" });
  }

  function updateValue(inputId: string, value: number | string) {
    trackStart();
    setSubmitted(false);
    setValues((current) => ({ ...current, [inputId]: value }));
    setTouchedIds((current) => (current.includes(inputId) ? current : [...current, inputId]));
  }

  function submit() {
    setSubmitted(true);
    const primaryBand = evaluation.metrics.find((metric) => metric.band)?.band?.id ?? "calculated";
    captureGamificationResultForCrm(tracking, primaryBand, {
      budget_stage_bucket: evaluation.trackingPayload.budget_stage_bucket,
    });
    trackGamificationEngagement(tracking, `${tracking.componentId}.result`, {
      ...evaluation.trackingPayload,
      xp_zone: "panel",
    });
  }

  function reset() {
    setValues(initialValues(schema));
    setTouchedIds([]);
    setSubmitted(false);
    startedRef.current = false;
  }

  return (
    <section
      id={schema.id}
      className="redesign-section redesign-section--muted gamification-tool"
      aria-labelledby={`${schema.id}-title`}
      data-gamification-tool={schema.id}
      {...gamificationTrackingAttributes(tracking)}
    >
      <div className="container">
        <div className="marketing-section-heading marketing-section-heading--left">
          <p className="marketing-eyebrow">{copy.eyebrow}</p>
          <h2 id={`${schema.id}-title`}>{copy.title}</h2>
          <p>{copy.intro}</p>
        </div>

        <form
          className="gamification-tool__grid"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="gamification-tool__panel redesign-list-card">
            <div className="gamification-tool__fields">
              {schema.inputs.map((input) => (
                <label className="gamification-tool__field" key={input.id}>
                  <span>{input.label}</span>
                  {input.description ? <small>{input.description}</small> : null}
                  {input.kind === "number" ? (
                    <input
                      type="number"
                      min={input.min}
                      max={input.max}
                      step={input.step ?? 1}
                      value={Number(values[input.id] ?? input.defaultValue)}
                      onChange={(event) => updateValue(input.id, Number(event.currentTarget.value))}
                    />
                  ) : (
                    <select
                      value={String(values[input.id] ?? input.defaultOptionId)}
                      onChange={(event) => updateValue(input.id, event.currentTarget.value)}
                    >
                      {input.options.map((option) => (
                        <option value={option.id} key={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </label>
              ))}
            </div>
            <div className="gamification-tool__actions">
              <button type="submit" className="marketing-button marketing-button--primary">
                {copy.calculateLabel}
              </button>
              <button type="button" className="gamification-tool__link-button" onClick={reset}>
                {copy.resetLabel}
              </button>
            </div>
          </div>

          <PlayableExperienceCanvas
            ariaLabel={copy.title}
            id={schema.id}
            nodes={canvasNodes}
            primaryLabel={submitted ? primaryBandLabel ?? copy.resultLabel : copy.title}
            progress={submitted ? 100 : inputProgress}
            secondaryLabel={submitted ? evaluation.metrics[0]?.displayValue : copy.intro}
            tracks={canvasTracks}
            variant="calculator"
          />

          {submitted ? (
            <aside className="gamification-tool__result redesign-list-card" aria-live="polite">
              <p className="marketing-eyebrow">{copy.resultLabel}</p>
              <dl className="gamification-tool__result-stats">
                {evaluation.metrics.map((metric) => (
                  <div key={metric.id}>
                    <dt>{metric.label}</dt>
                    <dd>{metric.displayValue}</dd>
                    {metric.band ? (
                      <small>
                        {copy.bandLabel}: {metric.band.label}
                      </small>
                    ) : null}
                  </div>
                ))}
              </dl>
            </aside>
          ) : null}
        </form>
      </div>
    </section>
  );
}
